import { useRoute } from 'vue-router';
import { onMounted, onBeforeUnmount, watch, nextTick, ref } from 'vue';
import store from '@shared/store/index';
import { checkUpdatedData } from '@shared/api/data/checkUpdatedData';

const UPDATE_LIMIT = {
  TAB_INTERVAL: 2 * 60 * 1000,
  FOCUS_INTERVAL: 10 * 60 * 1000,
  DEFAULT_INTERVAL: 5 * 60 * 1000,
  INACTIVITY_TIMEOUT: 5 * 60 * 1000,
  WAKE_DETECTION_THRESHOLD: 70 * 1000,
  HALF_HOUR: 30 * 60 * 1000
};

const updateState = {
  count: 0,
  timer: null,
  isUpdating: false
};

export function useAutoUpdate(pathCheck = '/') {
  const lastTriggerTime = ref({
    focus: 0,
    visibility: 0,
    interval: 0,
    path: 0,
    inactivity: 0,
    wake: 0,
    firstPageRefresh: Number(sessionStorage.getItem('lastFirstPageRefresh')) || 0
  });

  const route = useRoute();
  let activityCheckTimer = null;
  let lastTimerTick = Date.now();

  const getLastUpdateTime = () => {
    return Number(sessionStorage.getItem('lastAutoUpdate')) || Math.floor(Date.now() / 1000);
  };

  const setLastUpdateTime = (timestamp) => {
    sessionStorage.setItem('lastAutoUpdate', timestamp);
  };

  const getLastFirstPageRefresh = () => {
    return Number(sessionStorage.getItem('lastFirstPageRefresh')) || 0;
  };

  const setLastFirstPageRefresh = (timestamp) => {
    sessionStorage.setItem('lastFirstPageRefresh', timestamp);
    lastTriggerTime.value.firstPageRefresh = timestamp;
  };

  const canUpdate = (trigger) => {
    const now = Date.now();
    if (updateState.isUpdating) return false;
    if (!store.getters['UserSystem/hasPaidAccess']) return false;

    const timePassed = now - lastTriggerTime.value[trigger];
    switch (trigger) {
      case 'focus':
      case 'visibility':
      case 'interval':
      case 'path':
        return timePassed >= 30 * 1000;
      case 'inactivity':
        return timePassed >= UPDATE_LIMIT.INACTIVITY_TIMEOUT;
      case 'wake':
        return timePassed >= 60 * 1000;
      case 'firstPageRefresh':
        return timePassed >= UPDATE_LIMIT.HALF_HOUR;
      default:
        return false;
    }
  };

  const markTriggered = (trigger) => {
    lastTriggerTime.value[trigger] = Date.now();
  };

  const detectWakeUp = () => {
    const now = Date.now();
    const diff = now - lastTimerTick;

    if (diff > UPDATE_LIMIT.WAKE_DETECTION_THRESHOLD && canUpdate('wake')) {
      markTriggered('wake');
      doUpdate('wake');
    }

    lastTimerTick = now;
  };

  const onUserActivity = () => {
    if (canUpdate('inactivity')) {
      markTriggered('inactivity');
      doUpdate('inactivity');
    }
  };

  const onFocus = () => {
    if (document.visibilityState === 'visible' && canUpdate('focus')) {
      markTriggered('focus');
      doUpdate('focus');
    } else {
      checkOnWakeViaRAF();
    }
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      if (canUpdate('visibility')) {
        markTriggered('visibility');
        doUpdate('visibility');
      }
      checkHalfHourRefresh();
    }
  };

  const checkOnWakeViaRAF = () => {
    requestAnimationFrame(() => {
      if (canUpdate('wake')) {
        markTriggered('wake');
        doUpdate('wake');
      }
    });
  };

  const onRouteChange = () => {
    if (route.path === pathCheck && canUpdate('path')) {
      markTriggered('path');
      doUpdate('path');
    }
    checkHalfHourRefresh();
  };

  const startInterval = () => {
    clearInterval(updateState.timer);
    lastTimerTick = Date.now();
    updateState.timer = setInterval(() => {
      detectWakeUp();
      if (canUpdate('interval')) {
        markTriggered('interval');
        doUpdate('interval');
      }
      checkHalfHourRefresh();
    }, UPDATE_LIMIT.DEFAULT_INTERVAL);
  };

  const doUpdate = async (source) => {
    if (updateState.isUpdating) return;
    updateState.isUpdating = true;
    console.info(`[AutoUpdate] Обновление по триггеру: ${source}`);

    try {
      updateState.count++;
      const lastTime = getLastUpdateTime();
      const result = await checkUpdatedData(lastTime);
      if (!result) return;
      setLastUpdateTime(result.unix_datetime_now);
      await processUpdates(result);
    } catch (error) {
      console.error('[AutoUpdate] Ошибка:', error);
    } finally {
      updateState.isUpdating = false;
    }
  };

  const processUpdates = async ({ alpha, news, comments, signals, market_pulse, notifications }) => {
    await Promise.all([
      updateAlpha(alpha),
      updateNews(news),
      updateSignals(signals)
    ]);

    if (Array.isArray(comments)) {
      comments.reverse().forEach(comment => {
        const area = store.state.ContentSystem.Alpha.list[comment.object_pk] ? 'Alpha' : 'Infonoise';
        store.commit(`ContentSystem/${area}/toChangeLastComment`, { comment });
        store.commit(`ContentSystem/${area}/toIncreaseCommentCount`, {
          id: comment.object_pk,
          count: comment.comments_count
        });
      });
    }

    if (market_pulse?.btc_price) {
      store.commit('MarketPulse/toUpdateBtcPrice', {
        price_usd: market_pulse.btc_price.price_usd,
        change_1d_usd: market_pulse.btc_price.change_1d_usd
      });
    }

    if (notifications) {
      store.commit('Notifications/recalcNotificationsCount', notifications);
    }
  };

  const updateAlpha = async (alpha) => {
    if (alpha && Object.keys(alpha).length > 0) {
      await store.dispatch('ContentSystem/Alpha/addItemsToList', { alpha, isNew: true });
      await store.dispatch('ContentSystem/Alpha/loadAlphaData');
      await nextTick();
    }
  };

  const updateNews = async (news) => {
    if (news && Object.keys(news).length > 0) {
      await store.dispatch('ContentSystem/Infonoise/addItemsToList', { infonoise: news, isNew: true });
      await store.dispatch('ContentSystem/Infonoise/loadInfonoiseData');
      await nextTick();
    }
  };

  const updateSignals = async (signals) => {
    if (signals && Object.keys(signals).length > 0) {
      await store.dispatch('ContentSystem/refreshLastSignals', signals);
      await nextTick();
    }
  };

  const checkHalfHourRefresh = async () => {
    const now = Date.now();
    if (canUpdate('firstPageRefresh')) {
      await store.dispatch('ContentSystem/toRefreshFirstPageData');
      setLastFirstPageRefresh(now);
    }
  };

  const startAutoUpdate = () => {
    setLastUpdateTime(getLastUpdateTime());
    startInterval();
    onRouteChange();

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    window.addEventListener('mousemove', onUserActivity);
    window.addEventListener('scroll', onUserActivity);

    activityCheckTimer = setInterval(() => {
      onUserActivity();
    }, UPDATE_LIMIT.INACTIVITY_TIMEOUT);
  };

  const manualCheck = () => {
    doUpdate('manual');
  };

  watch(() => route.path, onRouteChange);

  onMounted(() => {
    onRouteChange();
  });

  onBeforeUnmount(() => {
    clearInterval(updateState.timer);
    clearInterval(activityCheckTimer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('mousemove', onUserActivity);
    window.removeEventListener('scroll', onUserActivity);
  });

  return {
    startAutoUpdate,
    manualCheck
  };
}